import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FaNodeJs, 
  FaReact, 
  FaDatabase, 
  FaFigma, 
  FaJsSquare,
  FaPython,
  FaCheckCircle,
  FaArrowRight,
  FaLaptopCode,
  FaVolumeMute,
  FaVolumeUp
} from "react-icons/fa";
import { 
  SiAntdesign, 
  SiMui,
  SiTypescript,
  SiExpress,
  SiFastapi
} from "react-icons/si";
import "./WebDevelopment.css";
import LogoLoop from "../components/LogoLoop/LogoLoop";
import WebDevProgressNavigator from "../components/WebDevProgressNavigator";

const LaptopSlideshow = ({ images, altText, autoPlayInterval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, autoPlayInterval);
    return () => clearInterval(interval);
  }, [images.length, autoPlayInterval, isHovered]);

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index, e) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };

  return (
    <div 
      className="laptop-mockup"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="laptop-screen">
        {/* Glass reflection overlay */}
        <div className="screen-reflection" />
        
        {/* Screen Track */}
        <div 
          className="screen-track" 
          style={{ 
            transform: `translate3d(-${currentIndex * 100}%, 0, 0)`
          }}
        >
          {images.map((img, idx) => (
            <div className="screen-slide" key={idx}>
              <img
                src={img}
                alt={`${altText} - Slide ${idx + 1}`}
                className="slider-image"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Interactive Navigation Arrows */}
        <button className="slide-arrow prev-arrow" onClick={handlePrev} aria-label="Previous image">
          <svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
        </button>
        <button className="slide-arrow next-arrow" onClick={handleNext} aria-label="Next image">
          <svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
        </button>

        {/* Interactive Progress Indicator */}
        <div className="screen-indicators">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`indicator-dot ${idx === currentIndex ? "active" : ""}`}
              onClick={(e) => handleDotClick(idx, e)}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
      
      {/* Laptop Base Deck */}
      <div className="laptop-base">
        <div className="laptop-base-reflection" />
      </div>
    </div>
  );
};

const LaptopVideo = ({ videoSrc, altText }) => {
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = (e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  };

  const handleDoubleClick = (e) => {
    const video = e.target;
    if (video) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) { // Safari
        video.webkitRequestFullscreen();
      } else if (video.msRequestFullscreen) { // IE11
        video.msRequestFullscreen();
      }
    }
  };

  return (
    <div className="laptop-video-wrapper" style={{ position: "relative", width: "100%" }}>
      <div className="laptop-mockup">
        <div className="laptop-screen">
          {/* Glass reflection overlay */}
          <div className="screen-reflection" />
          
          <video
            src={videoSrc}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            onDoubleClick={handleDoubleClick}
            className="slider-image"
            title="Double-click to expand to full screen"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              background: "#000",
              cursor: "pointer",
              pointerEvents: "auto"
            }}
          />
        </div>
        
        {/* Laptop Base Deck */}
        <div className="laptop-base">
          <div className="laptop-base-reflection" />
        </div>

        {/* Small Speaker Mockup Toggle */}
        <button
          onClick={toggleMute}
          className={`speaker-mockup-btn ${isMuted ? "muted" : "unmuted"}`}
          title={isMuted ? "Enable Sound" : "Mute Sound"}
          style={{
            position: "absolute",
            bottom: "12px",
            right: "-16px",
            width: "44px",
            height: "44px",
            borderRadius: "50%",
            background: isMuted ? "var(--brand-deep, #2d2d2a)" : "#10b981",
            color: "#ffffff",
            border: "none",
            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            zIndex: 10
          }}
        >
          {isMuted ? <FaVolumeMute size={18} /> : <FaVolumeUp size={18} />}
        </button>
      </div>
    </div>
  );
};

const WebDevelopment = () => {
  const navigate = useNavigate();

  // Arrays of images for each section
  const farImages = [
    "/images/FAR/image-1.png",
    "/images/FAR/image-2.jpeg",
    "/images/FAR/image-3.jpeg",
    "/images/FAR/image-4.jpeg",
    "/images/FAR/image-5.jpeg",
  ];

  const transportImages = [
    "/UPDATED-IMAGES/Logisitic/log1.png",
    "/UPDATED-IMAGES/Logisitic/log 2.png",
    "/UPDATED-IMAGES/Logisitic/log3.png",
    "/UPDATED-IMAGES/Logisitic/log4.png",
    "/UPDATED-IMAGES/Logisitic/log5.png",
    "/UPDATED-IMAGES/Logisitic/log6.png",
    "/UPDATED-IMAGES/Logisitic/log7.png",
    "/UPDATED-IMAGES/Logisitic/log8.png",
    "/UPDATED-IMAGES/Logisitic/log9.png",
    "/UPDATED-IMAGES/Logisitic/log10.png",
    "/UPDATED-IMAGES/Logisitic/log11.png",
  ];

  const prImages = [
    "/UPDATED-IMAGES/pr/pr1.png",
    "/UPDATED-IMAGES/pr/pr2.png",
    "/UPDATED-IMAGES/pr/pr3.png",
    "/UPDATED-IMAGES/pr/pr4.png",
    "/UPDATED-IMAGES/pr/pr5.png",
    "/UPDATED-IMAGES/pr/pr6.png",
    "/UPDATED-IMAGES/pr/pr7.png",
    "/UPDATED-IMAGES/pr/pr8.png",
    "/UPDATED-IMAGES/pr/pr9.png",
    "/UPDATED-IMAGES/pr/pr10.png",
  ];

  const eWarehouseImages = [
    "/images/webimage/ewhere/1.png",
    "/images/webimage/ewhere/2.png",
    "/images/webimage/ewhere/3.png",
    "/images/webimage/ewhere/4.png",
    "/images/webimage/ewhere/5.png",
    "/images/webimage/ewhere/6.png",
  ];

  const team360Images = [
    "/images/team-360/image-1.png",
    "/images/team-360/image-2.png",
    "/images/team-360/image-3.png",
    "/images/team-360/image-4.png",
    "/images/team-360/image-5.png",
    "/images/team-360/image-6.png",
  ];

  const oneManTwoMachineImages = [
    "/images/1man-2machine/image-1.png",
    "/images/1man-2machine/image-2.png",
    "/images/1man-2machine/image-3.png",
    "/images/1man-2machine/image-4.png",
    "/images/1man-2machine/image-5.png",
    "/images/1man-2machine/image-6.png",
  ];

  const autoBackupImages = [
    "/images/Autobackup-images/1.png",
    "/images/Autobackup-images/2.png",
    "/images/Autobackup-images/3.png",
    "/images/Autobackup-images/4.png",
    "/images/Autobackup-images/5.png",
  ];

  const autoDeleteImages = [
    "/images/Autodeletion-images/1.png",
    "/images/Autodeletion-images/2.png",
    "/images/Autodeletion-images/3.png",
  ];

  const outstandingImages = [
    "/UPDATED-IMAGES/outstanding/Outsanding1.png",
    "/UPDATED-IMAGES/outstanding/oustanding 2.png",
    "/UPDATED-IMAGES/outstanding/oustanding 3.png",
    "/UPDATED-IMAGES/outstanding/oustanding 4.png",
    "/UPDATED-IMAGES/outstanding/oustanding 5.png",
  ];

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      }
    }
  }, []);

  return (
    <div className="web-page-wrapper">
      {/* Floating progress navigator for Web Development projects */}
      <WebDevProgressNavigator />

      {/* ── Cinematic Full-Width Header Banner ── */}
      <header className="web-header" style={{ backgroundImage: 'linear-gradient(rgba(15, 23, 42, 0.45), rgba(15, 23, 42, 0.65)), url("/web-dev-cover/web-dev-cover.png")' }}>
        <div className="web-header-overlay-content">
          <h1>Web Development</h1>
        </div>
      </header>

      <div className="web-page-container web-root">
        
        {/* ── Header Description & Tagline Below Cover ── */}
        <div className="web-intro-section">
          <div className="header-tagline-badge">
            <span className="live-dot" /> Enterprise Web Engineering
          </div>
          <p className="web-intro-text">
            Expertly crafted digital solutions, database tools, and management dashboards tailored to your business needs.
          </p>
        </div>

      <div className="web-content-sections">
        
        {/* ── Fixed Asset Register Section ── */}
        <section className="web-service-section" id="far-web">
          <div className="web-text">
            <span className="section-label">Finance & Assets</span>
            <h3>Fixed Asset Register</h3>
            <p>
              Our Fixed Asset Register (FAR) module provides a comprehensive solution for tracking 
              and managing your organization's physical assets. From acquisition to disposal, 
              maintain detailed records, calculate depreciation, and ensure compliance with 
              ease and precision.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Straight-Line & Written-Down depreciation calculators</li>
              <li><FaCheckCircle className="li-check" /> Detailed asset auditing & lifecycle logs</li>
              <li><FaCheckCircle className="li-check" /> Dynamic compliance reporting for enterprise audits</li>
            </ul>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={farImages} altText="Fixed Asset Register" />
          </div>
        </section>

        {/* ── PR Purchase Section (with Live Launch button) ── */}
        <section className="web-service-section" id="pr-web">
          <div className="web-text">
            <span className="section-label">Operations & Procurement</span>
            <h3>Purchase Request Modules</h3>
            <p>
              The PR Purchase system simplifies procurement processes, providing a user-friendly 
              interface for managing purchase requests, approvals, and vendor interactions 
              effectively, ensuring transparency at every stage.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Multi-tier department approval workflows</li>
              <li><FaCheckCircle className="li-check" /> Automated email triggers & PDF invoice builders</li>
              <li><FaCheckCircle className="li-check" /> Real-time purchase request audits & tracking sheet</li>
            </ul>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={prImages} altText="Purchase Request System" autoPlayInterval={1500} />
          </div>
        </section>

        {/* ── Team 360 Section ── */}
        <section className="web-service-section" id="team360-web">
          <div className="web-text">
            <span className="section-label">Team & Business Insights</span>
            <h3>Team 360 Portal</h3>
            <p>
              Team 360 is an enterprise operational management portal. It connects teams with real-time operations, 
              streamlining attendance logging, client quote pipelines, and key financial analytics for a complete 
              360-degree business view.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Attendance Management Sync — Seamless attendance logs and Tally integration</li>
              <li><FaCheckCircle className="li-check" /> Quote Follow-ups — Automated client quote tracking, dispatch, and pipelines</li>
              <li><FaCheckCircle className="li-check" /> Useful Insight Reports — Advanced data analytics and business dashboards</li>
              <li><FaCheckCircle className="li-check" /> Outstanding Reports — Real-time ledger balances and payment tracking</li>
            </ul>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={team360Images} altText="Team 360 Portal" />
          </div>
        </section>

        {/* ── Transport Section (with Live Launch button) ── */}
        <section id="logistic-web" className="web-service-section">
          <div className="web-text">
            <span className="section-label">Supply Chain & Transport</span>
            <h3>Logistics Modules</h3>
            <p>
              Our Transport Web Application streamlines logistics and transportation management, 
              allowing businesses to optimize routes, manage shipments, and enhance overall 
              operational efficiency with real-time data.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Live shipment status sheets & routing</li>
              <li><FaCheckCircle className="li-check" /> Driver dispatch logging & manifest builders</li>
              <li><FaCheckCircle className="li-check" /> Freight metrics grids & expense trackers</li>
            </ul>
            <button className="btn-launch-app" onClick={() => navigate('/MobileDeveloper#logistic')}>
              Check Logistics Mobile App <FaArrowRight />
            </button>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={transportImages} altText="Logistics Modules" />
          </div>
        </section>

        {/* ── E-warehouse Section (with Live Launch button) ── */}
        <section id="e-ware-web" className="web-service-section">
          <div className="web-text">
            <span className="section-label">Tally ERP Workflow & Documents</span>
            <h3>e-Warehouse Web Portal</h3>
            <p>
              The e-Warehouse web application acts as a centralized document management hub integrated with Tally ERP. 
              Within this portal, unique transaction QR codes are dynamically generated. Users can easily manage, 
              view, and upload files directly inside the web interface, as well as synchronize with the mobile application 
              to attach images and document uploads scanned from mobile devices.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Dynamic QR code generator for secure Tally transaction mapping</li>
              <li><FaCheckCircle className="li-check" /> Direct web portal file management and document uploads</li>
              <li><FaCheckCircle className="li-check" /> Real-time synchronization with the companion mobile scanning application</li>
            </ul>
            <button className="btn-launch-app" onClick={() => navigate('/MobileDeveloper#e-ware')}>
              Check e-Warehouse Mobile App <FaArrowRight />
            </button>
          </div>
          <div className="web-image">
            <LaptopVideo videoSrc="/e-warehouse/e-ware-house.mp4" altText="e-Warehouse Web Portal" />
          </div>
        </section>

        {/* ── One Man Two Machine Section ── */}
        <section className="web-service-section" id="1m2m-web">
          <div className="web-text">
            <span className="section-label">Operator Efficiency & Incentives</span>
            <h3>One Man Two Machine (1M2M) Portal</h3>
            <p>
              Our 1M2M Manufacturing Portal is a highly optimized efficiency and production tracker. 
              Designed for dual-machine operator cells, it tracks output progress, calculates individual operator efficiencies, 
              and automatically computes performance-based incentives.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Operator Efficiency Tracking — Monitor real-time progress, cycle completion times, and outcomes across both active machines</li>
              <li><FaCheckCircle className="li-check" /> Automated Incentive Calculations — Dynamic formulas that calculate financial incentives based on operator yield and output targets</li>
              <li><FaCheckCircle className="li-check" /> Output & Progress Dashboard — Comprehensive analytics showing operator productivity curves and daily machine outcomes</li>
              <li><FaCheckCircle className="li-check" /> Live Performance Metrics — Real-time tracking of machine runtimes, active status syncs, and operator workload splits</li>
            </ul>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={oneManTwoMachineImages} altText="One Man Two Machine Portal" />
          </div>
        </section>

        {/* ── Outstanding Reports Section ── */}
        <section className="web-service-section" id="outstanding-web">
          <div className="web-text">
            <span className="section-label">Financial Analytics & Collections</span>
            <h3>Outstanding Reports & Collections Portal</h3>
            <p>
              Our Outstanding Reports Portal is an enterprise-grade financial intelligence application. 
              Fully integrated with ledger records, it provides credit managers and leadership with real-time receivables tracking, 
              age-wise aging schedules, pending bill dashboards, and automated payment follow-ups.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Receivables & Payables Ledger — Real-time tracking of pending bills, credits, and customer account balances</li>
              <li><FaCheckCircle className="li-check" /> Age-Wise Aging Schedules — Automatic division of outstanding balances into 30, 60, 90+ day buckets for risk assessment</li>
              <li><FaCheckCircle className="li-check" /> Collection Pipeline Tracker — Interactive workflow panels showing active payment collection statuses and follow-up logs</li>
              <li><FaCheckCircle className="li-check" /> Automated Payment Reminders — Direct system notifications and email builders for prompt customer reminders</li>
            </ul>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={outstandingImages} altText="Outstanding Reports & Collections Portal" />
          </div>
        </section>

        {/* ── Auto Backup Section ── */}
        <section className="web-service-section" id="backup-web">
          <div className="web-text">
            <span className="section-label">Data Protection & Integrity</span>
            <h3>Enterprise Auto Backup Software</h3>
            <p>
              Our Enterprise Auto Backup Application ensures the continuous safety of your critical files and systems. 
              Engineered for high performance, it automates standard backups, handles dual redundancy protocols (local and secure cloud), 
              and executes rapid, single-click disaster recovery operations.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Automated Scheduling — Program hourly, daily, weekly, or monthly backup cycles with complete hands-free execution</li>
              <li><FaCheckCircle className="li-check" /> Dual-Redundancy Sync — Secure data preservation split across local network drives and cloud storage endpoints</li>
              <li><FaCheckCircle className="li-check" /> High-Speed Restoration — Zero-data-loss restoration tools ensuring minimal downtime in emergency scenarios</li>
              <li><FaCheckCircle className="li-check" /> Smart Compression & Encryption — Advanced AES-256 standard data protection with efficient folder storage optimization</li>
            </ul>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={autoBackupImages} altText="Enterprise Auto Backup Software" />
          </div>
        </section>

        {/* ── Auto Delete Section ── */}
        <section className="web-service-section" id="delete-web">
          <div className="web-text">
            <span className="section-label">Storage Housekeeping & Optimization</span>
            <h3>Automated File Deletion & Clean-up Software</h3>
            <p>
              The Automated Deletion Application streamlines file storage optimization and folder housekeeping. 
              It runs in the background to automatically identify and purge unwanted old files in any specified directory path 
              based on customized maximum file count limits.
            </p>
            <ul className="web-specs">
              <li><FaCheckCircle className="li-check" /> Max File Count Housekeeping — Set exact threshold counts to automatically trigger cleaning when directories accumulate unwanted old files</li>
              <li><FaCheckCircle className="li-check" /> Directory Size Control — Automatically monitors and retains only a specified number of active files to reclaim disk space</li>
              <li><FaCheckCircle className="li-check" /> Folder Housekeeping — Periodically scans and cleans specified target folders to avoid manual clutter and storage bottlenecks</li>
              <li><FaCheckCircle className="li-check" /> Safety Exclusion Filters — Advanced folder patterns ensuring that crucial configuration records and target system assets are never purged</li>
            </ul>
          </div>
          <div className="web-image">
            <LaptopSlideshow images={autoDeleteImages} altText="Automated File Deletion & Clean-up Software" />
          </div>
        </section>
      </div>

      {/* ── Technology Section ── */}
      <section className="web-tech-section">
        <div className="web-tech-content">
          <div className="tech-badge">Powered by Modern Stacks</div>
          <h2>Our Technology Ecosystem</h2>
          <p>
            We leverage a cutting-edge technology ecosystem to build scalable, 
            high-performance web applications that drive business growth.
          </p>
          
          <div className="tech-loop-container">
            <LogoLoop
              logos={[
                { node: <FaReact style={{ color: '#61DAFB' }} />, title: "React.js" },
                { node: <FaJsSquare style={{ color: '#323330', backgroundColor: '#F7DF1E', borderRadius: '4px' }} />, title: "JavaScript" },
                { node: <SiTypescript style={{ color: '#3178C6' }} />, title: "TypeScript" },
                { node: <SiAntdesign style={{ color: '#0170FE' }} />, title: "Ant Design" },
                { node: <SiMui style={{ color: '#007FFF' }} />, title: "Material UI" },
                { node: <FaNodeJs style={{ color: '#339933' }} />, title: "Node.js" },
                { node: <SiExpress style={{ color: '#000000' }} />, title: "Express.js" },
                { node: <FaPython style={{ color: '#3776AB' }} />, title: "Python" },
                { node: <SiFastapi style={{ color: '#05998B' }} />, title: "FastAPI" },
                { node: <FaDatabase style={{ color: '#4479A1' }} />, title: "SQL Database" },
                { node: <FaFigma style={{ color: '#F24E1E' }} />, title: "Figma" },
              ]}
              speed={50}
              gap={80}
              logoHeight={50}
              direction="left"
              fadeOut={false}
              pauseOnHover={true}
              scaleOnHover={true}
            />
          </div>
        </div>

        <div className="diverse-services">
          <h2>Diverse Web App Design & Development</h2>
          <p>
            We have mastered web application development for over a decade. By keeping up 
            with the newest technologies, our development solutions stand out for their 
            robustness, data integrity, and extreme scalability.
          </p>
        </div>
      </section>
    </div>
    </div>
  );
};

export default WebDevelopment;
